import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { CoreConfigService } from '@core/services/config.service';
import { IUser, UserRole } from 'app/interfaces/user.interface';
import { ApiRoutes } from 'app/routes/app.routes';
import { Cipher, LocalStore } from 'app/utils/localstore.utils';

import { Router } from '@angular/router';
import { LoaderService } from 'app/services/loader/loader.service';
import { HttpService } from 'app/services/http/http.service';
import { SchoolContextService } from 'app/services/school/school-context.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit, OnDestroy {
  //  Public
  public coreConfig: any;
  public loginForm: UntypedFormGroup;
  public passwordTextType: boolean;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param {FormBuilder} _formBuilder
   * @param router
   * @param loaderService
   * @param httpService
   * @param schoolContextService
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _formBuilder: UntypedFormBuilder,
    private router: Router,
    public loaderService: LoaderService,
    private httpService: HttpService,
    private schoolContextService: SchoolContextService
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  /**
   * Toggle password
   */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  /**
   * On Submit
   */
  onSubmit() {
    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    this.httpService
      .makeRequest({
        method: 'post',
        path: ApiRoutes.api.user.login,
        options: {
          body: this.loginForm.value,
        },
        loader: {
          type: 'button',
        },
        isAuthRoutes: false
      })
      .subscribe((response) => {
        if (response?.status >= 200 && response?.status <= 300) {
          this.authActions(response);
        }
      });
  }

  authActions(response: any) {
    const responseData: IUser = response.data;
    LocalStore.setItem('token', responseData.refresh_token);
    LocalStore.setItem('x_api_key', responseData.x_api_key);
    LocalStore.setItem('user', responseData);

    // Initialize school context after successful login
    this.schoolContextService.updateFromUser(responseData);

    // this.state.setUserState(responseData);

    if (responseData.userType.toUpperCase() === UserRole.staff.toUpperCase()) {
      this.router.navigate(
        [`/app/staff/profile/${responseData?.id}`],
        // [`/app/staff-portal/teacher/${responseData?.id}`],
        {
          queryParams: {
            [responseData?.userType ?? UserRole.staff.toLowerCase()]:
              Cipher.encrypt(responseData?.id),
          },
        }
      );
      return;
    }

    if (
      responseData.userType.toUpperCase() === UserRole.learner.toUpperCase()
    ) {
      this.router.navigate(
        [`/app/learners/profile/${responseData?.id}`],
        {
          queryParams: {
            [responseData?.userType ?? UserRole.learner.toLowerCase()]:
              Cipher.encrypt(responseData?.id),
          },
        }
      );
      return;
    }

    this.router.navigate([`/app/${ApiRoutes.dashboard.home}`], {
      queryParams: {
        [responseData?.userType ?? UserRole.admin.toLowerCase()]:
          Cipher.encrypt(responseData?.id),
      },
    });

    return;
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.loginForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });

    // Subscribe to config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
